<template>
  <div>
    <div class="row">
      <div class="col-12 col-md-6">
        <!-- documentData carries `establishment` so the contacts picker suggests the
             contacts of the DESTINATION establishment, which is the addressee here. -->
        <documents-send-mail-form
          :documentData="mailDocument"
          :dispatchUrl="'equipmentTransfers/sendMail'"
          :toggleMailModal="toggleMailModal"
          :mailInformation="mailInformation"
        />
      </div>

      <div class="col-12 col-md-6">
        <h3 class="mb-3">{{ transfer.seq_number || transfer.code }}</h3>

        <dl class="row mb-2">
          <dt class="col-sm-5">
            {{ $t("EQUIPMENT_TRANSFERS.SOURCE_ESTABLISHMENT") }}
          </dt>
          <dd class="col-sm-7">
            {{ transfer.sourceEstablishment && transfer.sourceEstablishment.company_name }}
          </dd>
        </dl>
        <dl class="row mb-2">
          <dt class="col-sm-5">
            {{ $t("EQUIPMENT_TRANSFERS.DESTINATION_ESTABLISHMENT") }}
          </dt>
          <dd class="col-sm-7">
            {{
              transfer.destinationEstablishment &&
              transfer.destinationEstablishment.company_name
            }}
          </dd>
        </dl>
        <dl class="row mb-2">
          <dt class="col-sm-5">{{ $t("EQUIPMENT_TRANSFERS.TRANSFER_TYPE") }}</dt>
          <dd class="col-sm-7">
            {{
              transfer.transfer_type
                ? $t(`EQUIPMENT_TRANSFERS.TYPE_${transfer.transfer_type}`)
                : "-"
            }}
          </dd>
        </dl>
        <dl class="row mb-2">
          <dt class="col-sm-5">{{ $t("EQUIPMENT_TRANSFERS.TRANSFER_DATE") }}</dt>
          <dd class="col-sm-7">
            {{ transfer.transfer_date ? $formatDate(transfer.transfer_date, "LL") : "-" }}
          </dd>
        </dl>
        <dl class="row mb-2" v-if="transfer.transfer_type === 'TEMPORARY'">
          <dt class="col-sm-5">{{ $t("EQUIPMENT_TRANSFERS.RETURN_DATE") }}</dt>
          <dd class="col-sm-7">
            {{ transfer.return_date ? $formatDate(transfer.return_date, "LL") : "-" }}
          </dd>
        </dl>

        <table class="table table-sm mt-3">
          <thead class="thead-light">
            <tr>
              <th>{{ $t("EQUIPMENT_TRANSFERS.EQUIPMENT") }}</th>
              <th class="text-right">{{ $t("EQUIPMENT_TRANSFERS.QUANTITY") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in transfer.items || []" :key="item.id">
              <td>{{ item.equipment ? item.equipment.name : "-" }}</td>
              <td class="text-right">{{ item.quantity }}</td>
            </tr>
            <tr v-if="!transfer.items || !transfer.items.length">
              <td colspan="2" class="text-center text-muted">
                {{ $t("COMMON.NO_DATA") }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="d-flex justify-content-end">
      <el-button type="danger" @click="toggleMailModal">
        {{ $t("COMMON.CANCEL") }}
      </el-button>
    </div>
  </div>
</template>

<script>
import { Button } from "element-ui";
import { formatNumber, parseNumber } from "libphonenumber-js";
import DocumentsSendMailForm from "@/components/DocumentsSendMailForm";

const baseAppUrl = process.env.VUE_APP_BASE_URL;

export default {
  name: "equipment-transfer-mail-modal-content",

  components: {
    DocumentsSendMailForm,
    [Button.name]: Button,
  },

  props: {
    transfer: {
      type: Object,
      default: () => ({}),
    },
    toggleMailModal: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      mailInformation: {
        organization: {},
        recipient: {},
      },
    };
  },

  computed: {
    /**
     * The generic mail form resolves its contacts from `documentData.establishment`.
     * For an equipment transfer the addressee is the destination establishment,
     * which has to accept (or reject) the request.
     */
    mailDocument() {
      return {
        ...this.transfer,
        establishment: this.transfer.destinationEstablishment,
      };
    },
  },

  mounted() {
    this.refreshMailInformations();
  },

  watch: {
    transfer() {
      this.refreshMailInformations();
    },
  },

  methods: {
    formatPhoneNumber(phoneNumber) {
      try {
        return formatNumber(parseNumber(phoneNumber), "NATIONAL");
      } catch (e) {
        return phoneNumber;
      }
    },

    refreshMailInformations() {
      const user = this.$store.getters["profile/me"] || {};
      const destination = this.transfer.destinationEstablishment || {};

      // Deep link to the transfer inside the app (recipients are internal
      // managers), same pattern as the sales order submission mail.
      const route = this.$router.resolve({
        name: "View EquipmentTransfer",
        params: { id: this.transfer.id },
      });

      this.mailInformation = {
        organization: this.transfer.organization,
        subject: this.$t("COMMON.EMAIL_INFOS_SUBJECT_EQUIPMENT_TRANSFER", {
          organization: this.transfer.organization?.name || "",
          seq_number: this.transfer.seq_number || this.transfer.code || "",
        }),
        recipient: {
          username: destination.company_name,
          // The establishment mailbox, never `billing_email`: that one is the
          // client's billing address, which has no business receiving an
          // internal document.
          email: destination.email,
        },
        body: this.$t("COMMON.EQUIPMENT_TRANSFER_EMAIL_INFOS_BODY", {
          username: destination.company_name || "",
          source: this.transfer.sourceEstablishment?.company_name || "",
          transfer_type: this.transfer.transfer_type
            ? this.$t(`EQUIPMENT_TRANSFERS.TYPE_${this.transfer.transfer_type}`)
            : "",
          transfer_date: this.transfer.transfer_date
            ? this.$formatDate(this.transfer.transfer_date, "LL")
            : "",
          sender_name: `${user.firstname || ""} ${user.lastname || ""}`.trim(),
          phone: user.phone
            ? `${
                user.phone_type
                  ? this.$t(`COMMON.PHONE_TYPE_${user.phone_type}`) + ":"
                  : ""
              } ${this.formatPhoneNumber(user.phone)}${
                user.phone_extension ? ` (ext: ${user.phone_extension})` : ""
              }`
            : "",
          email: user.email || "",
          organization: this.transfer.organization?.name || "",
          link: `
            <p>
              <a
                href="${baseAppUrl + route.href}"
                rel="noopener noreferrer" target="_blank"
                class="button"
              >
                ${this.$t("EQUIPMENT_TRANSFERS.VIEW_TRANSFER")}
              </a>
            </p>
          `,
        }),
      };
    },
  },
};
</script>
